import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  CircularProgress,
  TextField,
  Checkbox,
  ListItemText,
  makeStyles,
  IconButton,
  Collapse,
  Box,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ViewColumnIcon from "@material-ui/icons/ViewColumn";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { getStandardButtonStyles } from "../../styles/standardButtons";

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  dialogPaper: {
    backgroundColor: theme.palette.type === "dark" ? "#1E2126" : "#FFFFFF",
    borderRadius: 16,
    border: `1px solid ${theme.palette.primary.main}40`,
    boxShadow: "0 16px 48px rgba(0, 0, 0, 0.45)",
    color: theme.palette.type === "dark" ? "#E2E8F0" : theme.palette.text.primary,
    overflow: "hidden",
  },
  dialogHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
    "& h2": {
      display: "flex",
      alignItems: "center",
      fontSize: "1.15rem",
      fontWeight: 600,
      margin: 0,
      color: theme.palette.type === "dark" ? "#FFFFFF" : theme.palette.text.primary,
    },
  },
  helpBox: {
    padding: theme.spacing(1.5, 3),
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(0, 180, 216, 0.08)" : "rgba(0, 180, 216, 0.05)",
    borderBottom: `1px solid ${
      theme.palette.type === "dark" ? "rgba(0, 180, 216, 0.2)" : "rgba(0, 180, 216, 0.15)"
    }`,
  },
  dialogContent: {
    padding: theme.spacing(2.5, 3),
  },
  dialogActions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(0, 0, 0, 0.2)" : "rgba(0, 0, 0, 0.02)",
    gap: 8,
  },
  multFieldLine: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  connectionsContainer: {
    marginTop: 16,
    border: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"
    }`,
    borderRadius: 8,
    overflow: "hidden",
  },
  connectionsHeader: {
    padding: "10px 14px",
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    fontWeight: 600,
    fontSize: "0.9rem",
    borderBottom: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"
    }`,
  },
  connectionItem: {
    display: "flex",
    alignItems: "center",
    padding: "4px 14px",
    cursor: "pointer",
    transition: "background-color 0.15s ease",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
    },
  },
}));

const KanbanBoardModal = ({ open, onClose, boardId, brandId, onSave }) => {
  const classes = useStyles();

  const initialState = {
    name: "",
    whatsappIds: [],
  };

  const [board, setBoard] = useState(initialState);
  const [whatsapps, setWhatsapps] = useState([]);
  const [isConnectionsExpanded, setIsConnectionsExpanded] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    const fetchWhatsapps = async () => {
      try {
        const { data } = await api.get("/whatsapp");
        const filtered = data.filter((w) => w.brandId === brandId);
        setWhatsapps(filtered);
      } catch (err) {
        toastError(err);
      }
    };
    if (brandId) fetchWhatsapps();
  }, [brandId]);

  useEffect(() => {
    const fetchBoard = async () => {
      if (!boardId) return;

      setLoading(true);
      try {
        const { data } = await api.get("/kanban-boards");
        const foundBoard = data.find((b) => b.id === boardId);
        if (foundBoard) {
          const wIds = foundBoard.whatsapps ? foundBoard.whatsapps.map((w) => w.id) : [];
          setBoard({ ...foundBoard, whatsappIds: wIds });
        }
      } catch (err) {
        toastError(err);
      }
      setLoading(false);
    };

    if (open) {
      if (boardId) {
        fetchBoard();
      } else {
        setBoard(initialState);
      }
    }
  }, [boardId, open]);

  const handleClose = () => {
    setShowHelp(false);
    onClose();
    setBoard(initialState);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setBoard((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!brandId) {
      toast.error(i18n.t("kanban.funnelModal.selectBrandFirst"));
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...board,
        brandId,
      };
      if (boardId) {
        await api.put(`/kanban-boards/${boardId}`, payload);
        toast.success(i18n.t("kanban.funnelModal.updated"));
      } else {
        await api.post("/kanban-boards", payload);
        toast.success(i18n.t("kanban.funnelModal.created"));
      }
      if (onSave) onSave();
      handleClose();
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  if (!open || !board) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: classes.dialogPaper,
      }}
    >
      <div className={classes.dialogHeader}>
        <h2>
          <ViewColumnIcon style={{ marginRight: 10, fontSize: 22, color: "#9b59b6" }} />
          {boardId ? i18n.t("kanban.funnelModal.editTitle") : i18n.t("kanban.funnelModal.createTitle")}
        </h2>
        <Box display="flex" alignItems="center">
          <IconButton
            size="small"
            onClick={() => setShowHelp((prev) => !prev)}
            title="Ayuda / Información"
            style={{ marginRight: 8, color: showHelp ? "#00b4d8" : "inherit" }}
          >
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
          <Typography
            variant="caption"
            style={{ opacity: 0.6, marginRight: 12, fontWeight: 500 }}
          >
            BotMix | CRM
          </Typography>
          <IconButton size="small" onClick={handleClose} aria-label="Cerrar">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </div>

      <Collapse in={showHelp}>
        <div className={classes.helpBox}>
          <Typography variant="body2" style={{ lineHeight: 1.5, opacity: 0.9 }}>
            ℹ️ {i18n.t("kanban.funnelModal.summary")}
          </Typography>
        </div>
      </Collapse>

      <DialogContent className={classes.dialogContent}>
        <form onSubmit={handleSubmit}>
          <div className={classes.multFieldLine}>
            <TextField
              label={i18n.t("kanban.funnelModal.funnelName")}
              name="name"
              value={board?.name || ""}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              autoFocus
              required
            />
          </div>

          <div className={classes.multFieldLine}>
            <div className={classes.connectionsContainer}>
              <div
                onClick={() => setIsConnectionsExpanded(!isConnectionsExpanded)}
                className={classes.connectionsHeader}
              >
                <span style={{ fontSize: "1.1rem", marginRight: "8px" }}>
                  {isConnectionsExpanded ? "📂" : "📁"}
                </span>
                <span>{i18n.t("kanban.funnelModal.brandConnections")}</span>
                <span style={{ marginLeft: "auto", fontSize: "0.8rem", opacity: 0.7 }}>
                  {i18n.t("kanban.funnelModal.connectionsCount", {
                    count: whatsapps.length,
                    selected: board?.whatsappIds?.length || 0,
                  })}
                </span>
              </div>

              {isConnectionsExpanded && (
                <div style={{ padding: "6px 0", maxHeight: "200px", overflowY: "auto" }}>
                  {whatsapps.length === 0 ? (
                    <div style={{ padding: "16px", textAlign: "center", opacity: 0.6 }}>
                      {i18n.t("kanban.funnelModal.noConnections")}
                    </div>
                  ) : (
                    whatsapps.map((whatsapp) => {
                      const isChecked = (board?.whatsappIds || []).indexOf(whatsapp.id) > -1;
                      return (
                        <div
                          key={whatsapp.id}
                          className={classes.connectionItem}
                          onClick={() => {
                            const currentIds = board?.whatsappIds || [];
                            const newIds = isChecked
                              ? currentIds.filter((id) => id !== whatsapp.id)
                              : [...currentIds, whatsapp.id];
                            handleChange({ target: { name: "whatsappIds", value: newIds } });
                          }}
                        >
                          <Checkbox
                            checked={isChecked}
                            color="primary"
                            style={{ padding: "4px 8px 4px 0" }}
                          />
                          <ListItemText
                            primary={whatsapp.name}
                            secondary={`ID: ${whatsapp.id} | Canal: ${whatsapp.channel}`}
                          />
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
        </form>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button onClick={handleClose} disabled={loading} className={classes.dangerButton}>
          {i18n.t("kanban.funnelModal.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading || !(board?.name || "").trim()}
          className={classes.applyButton}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : boardId ? (
            i18n.t("kanban.funnelModal.update")
          ) : (
            i18n.t("kanban.funnelModal.save")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KanbanBoardModal;
